import { useState, useEffect, useCallback } from "react";
import { useAuth, useApi } from "./AuthContext";
import {
  LayoutDashboard, UserPlus, DollarSign,
  ClipboardList, TrendingUp, Megaphone, Settings,
  LogOut, Menu, X, Leaf, ChevronRight, Image, Mail, Receipt, Briefcase, Users, Globe, Home
} from "lucide-react";
import MobileBottomNav from "./MobileBottomNav";

const NAV_ITEMS = [
  { id: "home", label: "Home", icon: Home, module: "__always__" },
  { id: "admin", label: "Admin Center", icon: LayoutDashboard, module: null },
  { id: "budgets", label: "Budgets", icon: DollarSign, module: "budgets" },
  { id: "clients", label: "Client Registration", icon: UserPlus, module: "clients" },
  { id: "expense-tracker", label: "Expense Tracker", icon: Receipt, module: "expense_tracker" },
  { id: "financial", label: "Financial", icon: TrendingUp, module: "financial" },
  { id: "leads", label: "Leads", icon: Megaphone, module: "leads", badgeKey: "leads" },
  { id: "website", label: "Website", icon: Globe, module: "portfolio" },
  { id: "sales-pipeline", label: "Sales Pipeline", icon: Briefcase, module: "sales_pipeline" },
  { id: "service-orders", label: "Service Orders", icon: ClipboardList, module: "service_orders", badgeKey: "soApprovals" },
  { id: "webmail", label: "Webmail", icon: Mail, module: "webmail" },
  { id: "teams", label: "Teams (Crews)", icon: Users, module: null },
  { id: "workforce", label: "Workforce", icon: Users, module: null },
];

const POLL_INTERVAL = 30000;

const BADGE_COLORS = {
  leads: "bg-red-500",
  soApprovals: "bg-green-500",
};

export default function DashboardLayout({ activeTab, onTabChange, children }) {
  const { user, logout, hasPermission, isAdmin } = useAuth();
  const api = useApi();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingLeads, setPendingLeads] = useState(0);
  const [pendingSOApprovals, setPendingSOApprovals] = useState(0);

  const fetchLeadCount = useCallback(async () => {
    if (!hasPermission("leads", "view")) return;
    try {
      const { data } = await api.get("/admin/leads/count");
      setPendingLeads(data.count);
    } catch (err) { /* silent */ }
  }, [api, hasPermission]);

  const fetchSOApprovalsCount = useCallback(async () => {
    if (!hasPermission("service_orders", "view")) return;
    try {
      const { data } = await api.get("/admin/service-orders/awaiting-release/count");
      setPendingSOApprovals(data.count);
    } catch (err) { /* silent */ }
  }, [api, hasPermission]);

  // Initial fetch + polling
  useEffect(() => {
    fetchLeadCount();
    fetchSOApprovalsCount();
    const interval = setInterval(() => {
      fetchLeadCount();
      fetchSOApprovalsCount();
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchLeadCount, fetchSOApprovalsCount]);

  // Refresh count when switching TO or FROM a relevant tab (allows badge to clear after action)
  useEffect(() => {
    if (activeTab === "leads") {
      const t = setTimeout(fetchLeadCount, 1500); return () => clearTimeout(t);
    }
    if (activeTab === "service-orders") {
      const t = setTimeout(fetchSOApprovalsCount, 1500); return () => clearTimeout(t);
    }
  }, [activeTab, fetchLeadCount, fetchSOApprovalsCount]);

  const badgeCounts = { leads: pendingLeads, soApprovals: pendingSOApprovals };

  const visibleItems = NAV_ITEMS.filter(item => {
    if (item.module === "__always__") return true;
    if (item.module === null) return isAdmin;
    return hasPermission(item.module, "view");
  });

  return (
    <div className="min-h-screen bg-stone-50 flex" data-testid="dashboard-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        data-testid="dashboard-sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-forest-700 text-white flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="px-5 py-5 border-b border-forest-600 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="w-6 h-6 text-forest-200" />
            <span className="font-serif text-lg font-bold text-white">GOW Admin</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-forest-200 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const badgeCount = item.badgeKey ? (badgeCounts[item.badgeKey] || 0) : 0;
            const badgeColor = item.badgeKey ? BADGE_COLORS[item.badgeKey] : "bg-red-500";

            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => { onTabChange(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-forest-600 text-white"
                    : "text-forest-200 hover:bg-forest-600/50 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1 text-left">{item.label}</span>
                {badgeCount > 0 && (
                  <span
                    data-testid={`${item.badgeKey}-badge`}
                    className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[11px] font-bold text-white animate-pulse ${badgeColor}`}
                  >
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
                {isActive && badgeCount === 0 && <ChevronRight className="w-4 h-4" />}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-forest-600">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-8 h-8 bg-forest-500 rounded-full flex items-center justify-center text-sm font-bold">
              {user?.name?.charAt(0) || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || "Admin"}</p>
              <p className="text-xs text-forest-300 truncate">{user?.role || "admin"}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-forest-200 hover:bg-forest-600/50 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-stone-200 px-4 lg:px-6 py-3 flex items-center gap-4 sticky top-0 z-30">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            className="hidden p-2 rounded-lg hover:bg-stone-100 text-stone-600"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-stone-800 flex items-center gap-2" data-testid="page-title">
            {NAV_ITEMS.find((n) => n.id === activeTab)?.label || "Dashboard"}
            {activeTab === "leads" && pendingLeads > 0 && (
              <span data-testid="header-leads-badge" className="min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full text-xs font-bold bg-red-500 text-white">
                {pendingLeads > 99 ? "99+" : pendingLeads}
              </span>
            )}
            {activeTab === "service-orders" && pendingSOApprovals > 0 && (
              <span data-testid="header-so-approvals-badge" className="min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full text-xs font-bold bg-green-500 text-white">
                {pendingSOApprovals > 99 ? "99+" : pendingSOApprovals}
              </span>
            )}
          </h1>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto pb-20 lg:pb-6">
          {children}
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        visibleItems={visibleItems}
        user={user}
        onLogout={logout}
        badgeCounts={{ ...badgeCounts, "service-orders": pendingSOApprovals, leads: pendingLeads }}
      />
    </div>
  );
}
